import { useAuthSecurity } from "@/hooks/useAuthSecurity";
import { Skeleton } from "@/components/ui/skeleton";
import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAdminContext } from "@/contexts/AdminContext";

const AUTH_RETURN_TO_KEY = "auth:return_to";

/**
 * Routes that must NEVER be gated by onboarding completion.
 * Emergency, rescue, scan, advisor, and upgrade flows bypass onboarding.
 */
const ONBOARDING_BYPASS_PREFIXES = [
  "/onboarding",
  "/emergency",
  "/vision",
  "/scan",
  "/ai-advisor", // advisor flows include urgent diagnosis
  "/upgrade",
  "/billing",
  "/pdf-delivery",
  "/library", // paid content access remains available before tank onboarding
  "/account",
  "/security",
];

function shouldBypassOnboarding(pathname: string) {
  return ONBOARDING_BYPASS_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

function normalizeRescueRoute(pathname: string, search: string) {
  const params = new URLSearchParams(search);
  const sessionId = params.get("session_id");

  if (pathname === "/emergency/rescue/session" && sessionId) {
    params.delete("session_id");
    const nextQuery = params.toString();
    return `/emergency/rescue/session/${sessionId}${nextQuery ? `?${nextQuery}` : ""}`;
  }

  return `${pathname}${search}`;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Home handles profile loading/errors and confirmed onboarding locally. */
  onboardingHandledByPage?: boolean;
}

export function ProtectedRoute({ children, onboardingHandledByPage = false }: ProtectedRouteProps) {
  const { user, loading } = useAuthSecurity();
  const { adminMode } = useAdminContext();
  const location = useLocation();
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  const [onboardingChecked, setOnboardingChecked] = useState(false);

  // Admin mode fully bypasses the onboarding gate.
  const bypassOnboarding = onboardingHandledByPage || adminMode || shouldBypassOnboarding(location.pathname);

  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setOnboardingChecked(false);
      setOnboardingCompleted(null);
      return;
    }

    // Skip the gating check entirely on bypass routes — emergency must never block.
    if (bypassOnboarding) {
      setOnboardingChecked(true);
      return;
    }

    (async () => {
      const { data, error } = await supabase.rpc("get_my_tank_profile", { p_user_id: user.id });
      if (cancelled) return;
      if (error) {
        console.error("[ONBOARDING_GATE] profile fetch failed", { error });
        // Fail closed: a brand-new user with no tank_profile row must be
        // routed to onboarding, not silently granted full app access.
        setOnboardingCompleted(false);
      } else {
        const profile = data as { onboarding_completed?: boolean | null } | null | undefined;
        const completed = !!profile && profile.onboarding_completed === true;
        console.log("[ONBOARDING_GATE] resolved", {
          user_id: user.id,
          pathname: location.pathname,
          onboarding_completed: completed,
          has_profile: !!data,
        });
        setOnboardingCompleted(completed);
      }
      setOnboardingChecked(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [user, bypassOnboarding, location.pathname]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Skeleton className="h-12 w-48" />
      </div>
    );
  }

  if (!user) {
    const attemptedRoute = normalizeRescueRoute(location.pathname, location.search);
    const attemptedUrl = `${attemptedRoute}${location.hash}`;
    const params = new URLSearchParams(location.search);
    const sessionId = location.pathname.startsWith("/emergency/rescue/session/")
      ? location.pathname.split("/").pop() ?? null
      : params.get("session_id");
    const incidentId = params.get("incident_id");

    sessionStorage.setItem(AUTH_RETURN_TO_KEY, attemptedUrl);

    console.log("[AUTH_REDIRECT] attempted protected route", {
      attempted_url: attemptedUrl,
      session_id: sessionId,
      incident_id: incidentId,
    });
    console.log("[AUTH_REDIRECT] saved return URL", {
      key: AUTH_RETURN_TO_KEY,
      return_to: attemptedUrl,
    });

    return <Navigate to={`/?return_to=${encodeURIComponent(attemptedUrl)}`} replace />;
  }

  // Onboarding gate: if user is authenticated and the route is not bypassed,
  // require onboarding_completed === true before rendering.
  if (!bypassOnboarding) {
    if (!onboardingChecked) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <Skeleton className="h-12 w-48" />
        </div>
      );
    }
    if (onboardingCompleted === false) {
      console.log("[ONBOARDING_GATE] redirecting to /onboarding", {
        user_id: user.id,
        from: location.pathname,
      });
      return <Navigate to="/onboarding" replace />;
    }
  }

  return <>{children}</>;
}
